import Download16 from '@carbon/icons-react/es/download/16';
import Globe16 from '@carbon/icons-react/es/globe/16';
import Settings16 from '@carbon/icons-react/es/settings/16';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
} from 'ag-grid-community';
import { Tooltip, Typography } from 'antd';
import moment from 'moment';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { ProjectFilesAPI } from 'api/project-files';
import { ProjectFileGroup } from 'api/project-files/types';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { Collapse } from 'components/atoms/collapse';
import { Panel } from 'components/atoms/collapse/panel';
import { Menu } from 'components/atoms/dropdown';
import { Grid } from 'components/atoms/grid';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { DownloadLink } from 'components/molecules/download-link/download-link';
import { toastService } from 'components/molecules/toast';
import {
  fileFormatNotAbleToPreview,
  nonImageFileToPreview,
} from 'components/organisms/image-preview';
import { BasicImagePreview } from 'components/organisms/image-preview/basic-image-preview';
import { RoutePaths } from 'constant';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUserNameGetter } from 'hooks/user-user-name';
import {
  getProjectFileGroupsQueryKey,
  useDeleteProjectFileGroupMutation,
  useDeleteProjectFileMutation,
} from 'queries/project-files';
import { handleDownload } from 'utils/attachment';
import { extname, isImageUrlFromExtension, openFileUrl } from 'utils/files';
import { FileExtensionIcon } from '../file-extension-icon';

interface TitleProps {
  isPrivate?: boolean;
}

const MoreOptions = styled.div`
  visibility: hidden;
`;

const Title = styled.div<TitleProps>`
  display: flex;
  align-items: center;
  color: var(--gray-80);
  font-weight: var(--font-weight-bold);
  padding: var(--spacing-03) 0 var(--spacing-03) var(--spacing-05);
  background: ${(props) => (props.isPrivate ? 'var(--yellow-10)' : '')};
  position: sticky;
  top: 0;
  width: 100%;

  svg,
  .ant-avatar {
    margin-right: var(--spacing-04);
  }

  &:hover {
    ${MoreOptions} {
      visibility: visible;
    }
  }
`;

const FileGroupMeta = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
`;

const GridWrapper = styled.div`
  margin-top: var(--layout-02);

  .ag-layout-auto-height .ag-center-cols-clipper {
    min-height: 80px;
  }

  display: ${(props: { rowCount: number | null }) =>
    props.rowCount === null || props.rowCount > 0 ? 'block' : 'none'};
`;

const StyledGrid = styled(Grid)`
  .ag-overlay-no-rows-wrapper {
    display: flex;
    text-align: unset;
    pointer-events: auto;
    padding-top: 30px !important;
    padding-left: var(--spacing-08);
  }

  .ag-row {
    .file-more-options {
      visibility: hidden;
    }
    &:hover {
      .file-more-options {
        visibility: visible;
      }
    }
  }

  .ag-react-container > :not(:first-child) {
    display: none !important;
  }
`;

const MoreOptionsMenuDivider = styled(Menu.Divider)`
  margin-left: var(--spacing-05) !important;
  margin-right: var(--spacing-05) !important;
`;

const EmptyStateButton = styled(Button)`
  padding-left: var(--spacing-02);
`;

const GridCollapse = styled(Collapse)`
  .ant-collapse-header {
    padding-right: 0 !important;
  }

  .ant-collapse-item-active {
    padding-bottom: 0 !important;
  }
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const FileName = styled.div`
  margin-left: var(--spacing-05);
  &:hover {
    cursor: pointer;
    color: var(--primary-color);
  }
`;

const StyledLink = styled.a`
  color: inherit;
`;

const FileGroupMoreOptions: FC<{
  group: ProjectFileGroup;
  uploadHandler: () => void;
  editHandler: () => void;
  userEmail: string;
}> = ({ group, editHandler }) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteGroup } = useDeleteProjectFileGroupMutation(
    ProjectId(group.projectId),
    group.folderId,
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(
          getProjectFileGroupsQueryKey(ProjectId(group.projectId)),
        );
        toastService({
          title: translate('project.folder.deleted.success.title'),
          message: translate('project.folder.deleted.success.message'),
        }).success();
      },
    },
  );

  const deleteConfirm = useDeleteConfirmModal({
    title: useTranslation('project.folder.delete.confirm.title'),
    content: useTranslation('project.folder.delete.confirm.message', {
      b: (chunks) => <Typography.Text strong>{chunks}</Typography.Text>,
    }),
    onOk: async () => {
      await deleteGroup();
    },
  });

  const menu = (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item
        key="settings"
        icon={<Settings16 />}
        disabled={group.isDefault}
        onClick={() => editHandler()}
      >
        <FormattedMessage id="project.folder.settings" />
      </Menu.Item>
      <MoreOptionsMenuDivider />
      <Menu.ItemDelete key="delete" onClick={deleteConfirm}>
        <FormattedMessage id="delete" />
      </Menu.ItemDelete>
    </Menu>
  );

  const moreOptionOnClick = (e) => e.stopPropagation();

  return (
    <MoreOptions onClick={moreOptionOnClick}>
      {/**
       * Temporarily commenting out upload option at folder level.
       */}
      {/* <Tooltip title={<FormattedMessage id="upload" />}>
        <UploadButton
          type="text"
          size="small"
          icon={<CloudUpload16 />}
          onClick={uploadHandler}
        />
      </Tooltip> */}
      <Tooltip
        title={<FormattedMessage id="download.as.zip" />}
        placement="left"
      >
        <span>
          <DownloadLink
            url={ProjectFilesAPI.getFileGroupDownloadUrl(
              ProjectId(group.projectId),
              group.folderId,
            )}
          >
            <Download16 />
          </DownloadLink>
        </span>
      </Tooltip>
      <MoreActionsDropdown menu={menu} />
    </MoreOptions>
  );
};

const MoreOptionsCellRenderer: FC<ICellRendererParams> = ({ data }) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteFile } = useDeleteProjectFileMutation(
    ProjectId(data.group.projectId),
    data.group.folderId,
    data.id,
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(
          getProjectFileGroupsQueryKey(ProjectId(data.group.projectId)),
        );

        toastService({
          title: translate('project.files.deleted.success.title'),
          message: translate('project.files.deleted.success.message'),
        }).success();
      },
    },
  );

  const deleteConfirm = useDeleteConfirmModal({
    title: useTranslation('project.files.delete.confirm.title'),
    content: useTranslation('project.files.delete.confirm.message'),
    onOk: async () => {
      await deleteFile();
    },
  });

  const menu = (
    <Menu>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      <Menu.Item key="edit" icon={<Download16 />}>
        <DownloadLink
          url={ProjectFilesAPI.getFileDownloadUrl(
            ProjectId(data.group.projectId),
            data.group.folderId,
            data.id,
          )}
        >
          <FormattedMessage id="download" />
        </DownloadLink>
      </Menu.Item>
      <MoreOptionsMenuDivider />
      <Menu.ItemDelete
        key="delete"
        onClick={deleteConfirm}
        disabled={data.members > 0}
      >
        <FormattedMessage id="delete" />
      </Menu.ItemDelete>
    </Menu>
  );

  return <MoreActionsDropdown menu={menu} />;
};

export const FileNameRenderer: FC<ICellRendererParams> = ({ data }) => {
  const [isImagePreview, setIsImagePreview] = useState(false);
  const downloadAttachmentErrorText = useTranslation(
    'attachments.download.error',
  );
  const history = useHistory();

  const [isImage, setIsImage] = useState(false);

  useEffect(() => {
    setIsImage(isImageUrlFromExtension(data.location || data.name));
  }, [data]);

  const openFileInWorkspace = useCallback(() => {
    if (data.id) {
      history.push(
        RoutePaths.attachmentWorkspace.replace(
          ':attachmentId',
          data.id.toString(),
        ),
      );
    }
  }, [data.id, history]);

  const onImageFileClicked = useCallback(() => {
    if (data.isAnnotationStarted) {
      openFileInWorkspace();
    } else {
      setIsImagePreview(true);
    }
  }, [data.isAnnotationStarted, openFileInWorkspace]);

  const onNonImageFileClicked = useCallback(
    (e) => {
      e.stopPropagation();
      const fileExtension = extname(data.location || data.fileName) ?? '';

      if (fileFormatNotAbleToPreview.includes(fileExtension.toLowerCase())) {
        openFileInWorkspace();
      } else if (nonImageFileToPreview.includes(fileExtension.toLowerCase())) {
        if (!data.isAnnotationStarted) {
          setIsImagePreview(true);
        } else {
          openFileInWorkspace();
        }
      } else {
        openFileUrl(e, data.id, downloadAttachmentErrorText);
      }
    },
    [
      data.location,
      data.fileName,
      data.isAnnotationStarted,
      data.id,
      openFileInWorkspace,
      downloadAttachmentErrorText,
    ],
  );

  return (
    <>
      <FileNameCell
        data-cy={
          data.group ? `${data.group.folderName}_${data.name}` : `${data.name}`
        }
      >
        <FileExtensionIcon name={data.name} />
        <FileName>
          {!isImage ? (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <StyledLink
              rel="noreferrer noopener"
              target="_blank"
              onClick={onNonImageFileClicked}
            >
              {data.name}
            </StyledLink>
          ) : (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <span onClick={onImageFileClicked}>{data.name}</span>
          )}
        </FileName>
      </FileNameCell>
      <BasicImagePreview
        data={data.id}
        fileName={data.name}
        src={data.location}
        preview={isImagePreview}
        onClose={() => setIsImagePreview(false)}
        alt={data.name}
        onDownload={() => handleDownload(data.id, downloadAttachmentErrorText)}
        isBasicPreview={false}
      />
    </>
  );
};

const EmptyState = ({
  isPrivate,
  onClick,
}: {
  isPrivate: boolean;
  onClick: () => void;
}) => {
  return (
    <>
      <FormattedMessage
        id={
          isPrivate
            ? 'project.file.groups.private.empty.state.message'
            : 'project.file.groups.empty.state.message'
        }
      />
      <EmptyStateButton type="link" onClick={onClick}>
        <b>
          <FormattedMessage id="project.file.groups.empty.state.add.link" />
        </b>
      </EmptyStateButton>
    </>
  );
};

const ProjectFileGroupGrid = ({
  group,
  searchInput,
  addFileToGroup,
  editFileGroup,
}: {
  group: ProjectFileGroup;
  searchInput: string;
  addFileToGroup: () => void;
  editFileGroup: () => void;
}) => {
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const [searchCount, setSearchCount] = useState<number | null>(null);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);
  const user = useUser();
  const getUserName = useUserNameGetter();
  const addedByText = useTranslation('added_by');
  const createdOnText = useTranslation('created_on');

  useEffect(() => {
    gridApi?.setQuickFilter(searchInput);
    setSearchCount(searchInput ? gridApi?.getDisplayedRowCount() || 0 : null);
  }, [gridApi, group.attachments, group.folderName, searchInput]);

  const rowData = useMemo(
    () =>
      group?.attachments
        .filter((_file) => _file.attachmentId)
        .map((_file) => ({
          id: _file.attachmentId,
          name: _file.name,
          created_on: moment(_file.createdAt).format('MMM DD, YYYY'),
          added_by: getUserName(_file.createdBy),
          group,
          userEmail: user.user.emailId,
          location: _file.location,
          versionMappingId: _file.versionMappingId,
          latestVersion: _file.latestVersion,
          commentThreadId: _file.commentThreadId,
          folderId: group.folderId,
          isAnnotationStarted: _file.isAnnotationStarted,
        }))
        .filter(Boolean) || [],
    [getUserName, group, user.user.emailId],
  );

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: 'name',
        flex: 1,
        cellRenderer: 'fileNameRenderer',
      },
      {
        field: 'added_by',
        width: 200,
        headerName: addedByText,
      },
      {
        field: 'created_on',
        width: 200,
        headerName: createdOnText,
      },
      {
        width: 48,
        cellClass: 'file-more-options',
        cellRenderer: 'moreOptionsCellRenderer',
        sortable: false,
      },
    ],
    [addedByText, createdOnText],
  );

  const frameworkComponents = useMemo(
    () => ({
      fileNameRenderer: memo(FileNameRenderer),
      moreOptionsCellRenderer: memo(MoreOptionsCellRenderer),
      EmptyState,
    }),
    [],
  );

  const noRowOverlayComponentParams = useMemo(
    () => ({
      isPrivate: group?.isPrivate,
      onClick: addFileToGroup,
    }),
    [addFileToGroup, group?.isPrivate],
  );

  const defaultColumnDef = useMemo(
    () => ({
      suppressMenu: true,
      sortable: true,
    }),
    [],
  );

  return (
    <>
      <GridWrapper rowCount={searchCount} data-cy={group?.folderName}>
        <GridCollapse
          defaultActiveKey={group.folderId}
          expandIcon={() => <></>}
        >
          <Panel
            key={group.folderId}
            header={
              <Title
                isPrivate={group?.isPrivate}
                className="project-files-folder-title"
              >
                <FileGroupMeta>
                  {group?.isDefault ? (
                    <Globe16 />
                  ) : (
                    <Avatar name={group?.folderName} size="small" />
                  )}
                  <div>
                    {group?.folderName} (
                    {searchCount != null
                      ? searchCount
                      : group?.attachments
                      ? group?.attachments.length
                      : 0}
                    )
                  </div>
                </FileGroupMeta>
                <FileGroupMoreOptions
                  group={group}
                  uploadHandler={addFileToGroup}
                  editHandler={editFileGroup}
                  userEmail={user.user.emailId}
                />
              </Title>
            }
          >
            <StyledGrid
              className="ag-theme-balham"
              animateRows
              suppressContextMenu
              suppressCellSelection
              suppressHorizontalScroll
              onGridReady={onGridReady}
              domLayout="autoHeight"
              noRowsOverlayComponent="EmptyState"
              noRowsOverlayComponentParams={noRowOverlayComponentParams}
              frameworkComponents={frameworkComponents}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColumnDef}
            />
          </Panel>
        </GridCollapse>
      </GridWrapper>
    </>
  );
};

interface ProjectFileGroupsProps {
  groups?: ProjectFileGroup[];
  searchInput: string;
  addFileToGroup: (id) => void;
  editFileGroup: (group) => void;
}

export const ProjectFileGroups: FC<ProjectFileGroupsProps> = ({
  groups,
  searchInput,
  addFileToGroup,
  editFileGroup,
}) => {
  const _addFileToGroup = useCallback(
    (_group) => {
      addFileToGroup(_group.folderId);
    },
    [addFileToGroup],
  );
  const _editFileGroup = useCallback(
    (_group) => {
      editFileGroup(_group);
    },
    [editFileGroup],
  );

  return (
    <>
      {groups?.map((_group) => (
        <ProjectFileGroupGrid
          key={_group.folderId}
          group={_group}
          searchInput={searchInput}
          addFileToGroup={() => _addFileToGroup(_group)}
          editFileGroup={() => _editFileGroup(_group)}
        />
      ))}
    </>
  );
};
